/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Checkbox,
  ClickAwayListener,
  Divider,
  Grid,
  Hidden,
  MenuItem,
  MenuList,
} from '@mui/material';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import {
  InboundNSIntegrationStatusEnum,
  InboundTaskItemStatusEnum,
} from 'commons/enums';
import {
  IInboundInvoice,
  IInboundItemIdentifier,
  IInboundTaskItem,
} from 'commons/types';
import { formatDate } from 'commons/utils/formatDate';
import { ChooseActionBtn } from 'pages/dashboard/commons/components';
import {
  completeInboundInvoiceApprovalAction,
  getWaitingApprovalInboundInvoice,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';
import { INBOUND_ROUTES } from 'routes/constants';

import { ItemBinDetailsModal, ItemRow } from './components';
import { RowMenuItemConfig } from './configs';
import S from './InboundInvoiceApprovalItemPage.styles';
import { IRowMenuItemData } from './interfaces';

export const InboundInvoiceApprovalItemPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const invoiceNumber = location.state?.invoiceNumber ?? null;
  const inboundTaskNumber = location.state?.inboundTaskNumber ?? null;

  const { inboundInvoice } = useSelector(
    (state: RootReducerInterface) => state.inboundInvoice,
  );

  // search bar states
  const [filter, setFilter] = useState<string>('');

  // checkbox states
  const [checkAll, setCheckAll] = useState<boolean>(false);
  const [checkedItems, setCheckedItems] = useState<IInboundItemIdentifier[]>(
    [],
  );

  // action button states
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const [openBulkDialog, setOpenBulkDialog] = useState<boolean>(false);
  const [openBulkDialogData, setOpenBulkDialogData] =
    useState<IRowMenuItemData | null>(null);

  // confirmation states
  const [openConfirmationDialog, setOpenConfirmationDialog] =
    useState<boolean>(false);

  // inbound invoice states
  const [dataFetched, setDataFetched] = useState<boolean>(false);
  useEffect(() => {
    if (invoiceNumber && inboundTaskNumber) {
      dispatch(
        getWaitingApprovalInboundInvoice({
          invoiceNumber,
          inboundTaskNumber,
          notFoundAction: () => navigate(INBOUND_ROUTES.APPROVAL_LIST),
        }),
      );
    } else {
      setDataFetched(true);
    }
  }, [dispatch, invoiceNumber, inboundTaskNumber, navigate]);
  const defaultInboundInvoice: IInboundInvoice = {
    /* fallback value, normally should not be assigned */
    invoice_date: new Date().toISOString(),
    inbound_task_number: '',
    invoice_number: '',
    warehouse_id: 0,
    integration_status: InboundNSIntegrationStatusEnum.RECEIVING,
    receiver: '',
  };
  const [displayedInboundInvoice, setDisplayedInboundInvoice] =
    useState<IInboundInvoice>(inboundInvoice || defaultInboundInvoice);

  useEffect(() => {
    if (inboundInvoice) {
      setDisplayedInboundInvoice(inboundInvoice);
      setDataFetched(true);
    }
  }, [inboundInvoice]);

  // item detail modal states
  const [showItemDetail, setShowItemDetail] = useState<IInboundTaskItem | null>(
    null,
  );

  // redirect to list page if there's no invoice
  if (!invoiceNumber || !inboundTaskNumber || !inboundInvoice) {
    if (dataFetched) navigate(INBOUND_ROUTES.APPROVAL_LIST);
    return <div />;
  }

  const handleClickMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = (): void => {
    setAnchorEl(null);
  };

  const handleOpenBulkDialog = (name: string): void => {
    setOpenBulkDialogData(RowMenuItemConfig[name]);
    setOpenBulkDialog(true);
  };
  const handleCloseBulkDialog = (): void => {
    setOpenBulkDialogData(null);
    setOpenBulkDialog(false);
  };

  const selectedItems = (displayedInboundInvoice.items || []).filter((item) =>
    checkedItems.some(
      (checkedItem) =>
        checkedItem.sku_code === item.sku_code && checkedItem.foc === item.foc,
    ),
  );
  const selectedSkus = selectedItems.map((item) => item.sku_code);

  const handleCheckItem = (skuCode: string, foc: boolean): void => {
    if (
      checkedItems.some((item) => item.sku_code === skuCode && item.foc === foc)
    ) {
      // remove from checkedItems
      checkedItems.splice(
        checkedItems.findIndex(
          (item) => item.sku_code === skuCode && item.foc === foc,
        ),
        1,
      );
      setCheckedItems([...checkedItems]);
    } else {
      // insert to checkedItems
      setCheckedItems([...checkedItems, { sku_code: skuCode, foc }]);
    }
  };

  const handleCheckAll = (): void => {
    if (checkAll) {
      // remove all check list
      setCheckedItems([]);
    } else {
      // add all item to checkedItems
      setCheckedItems(
        (displayedInboundInvoice.items || []).map((item) => {
          return { sku_code: item.sku_code, foc: item.foc };
        }),
      );
    }
    setCheckAll(!checkAll);
  };

  const onFilterChange = (value: string): void => {
    setFilter(value);
    if (value === '') {
      setDisplayedInboundInvoice(inboundInvoice);
    } else {
      const filteredIdx: number[] = [];
      const filteredItems = (inboundInvoice.items || []).filter((item, idx) => {
        if (item.sku_code.includes(value)) {
          filteredIdx.push(idx);
          return true;
        }
        return false;
      });
      setDisplayedInboundInvoice({
        ...inboundInvoice,
        items: filteredItems,
      });
    }
  };

  return (
    <div>
      <S.ContentWrapper>
        {/* search bar */}
        <S.SearchBar
          placeholder="SKU Number"
          variant="outlined"
          value={filter}
          onChange={(event) => onFilterChange(event.target.value)}
          inputProps={{
            'data-testid': 'InputApprovalSKUInbound',
          }}
        />

        {/* main info */}
        <S.InvoiceMainInfoWrapper container>
          <S.DocumentNumberText item xs={12} md={4} lg={2}>
            {displayedInboundInvoice.inbound_task_number}
          </S.DocumentNumberText>
          <S.InvoiceInfoText item xs={12} md={8} lg={10}>
            {displayedInboundInvoice.vendor}
          </S.InvoiceInfoText>
          <S.InvoiceInfoText
            item
            xs={12}
            data-testid="InboundApprovalInvoiceNumber"
          >
            {`Invoice Number: ${displayedInboundInvoice.invoice_number}`}
          </S.InvoiceInfoText>
          <S.InvoiceInfoText
            item
            xs={12}
            data-testid="InboundApprovalInvoiceDate"
          >
            {`Invoice Date: ${formatDate(
              displayedInboundInvoice.invoice_date,
              'dd/LL/yyyy',
            )}`}
          </S.InvoiceInfoText>
        </S.InvoiceMainInfoWrapper>

        {/* Action button */}
        <div style={{ width: '220px' }}>
          <ChooseActionBtn
            selectedData={selectedItems}
            anchorEl={anchorEl}
            openMenu={openMenu}
            handleClickMenu={handleClickMenu}
            dataName="item"
          >
            <ClickAwayListener onClickAway={handleCloseMenu}>
              <MenuList id="split-button-menu" autoFocusItem>
                {Object.keys(RowMenuItemConfig).map((configName) => (
                  <MenuItem
                    onClick={() => handleOpenBulkDialog(configName)}
                    key={`menu-item-${configName}`}
                  >
                    {configName}
                  </MenuItem>
                ))}
              </MenuList>
            </ClickAwayListener>
          </ChooseActionBtn>
        </div>

        {/* Table header */}
        <S.TaskItemWrapper container>
          <Grid item xs={1.5} sm={1}>
            <Checkbox
              data-testid="checkboxItemInboundApprovalHeader"
              onClick={() => handleCheckAll()}
              checked={checkAll}
            />
          </Grid>
          <S.TaskItemText item xs={3} sm={2}>
            SKU Code
          </S.TaskItemText>
          <Hidden only="xs">
            <S.TaskItemText item xs={2}>
              Name
            </S.TaskItemText>
            <S.TaskItemText item xs={1.5}>
              UOM
            </S.TaskItemText>
          </Hidden>
          <S.TaskItemText item xs={1.5} sm={1}>
            <div style={{ textAlign: 'center', paddingRight: 8 }}>QTY</div>
          </S.TaskItemText>
          <S.TaskItemText item xs={2.5} sm={1.5}>
            Status
          </S.TaskItemText>
          <S.TaskItemText item xs={3} style={{ textAlign: 'center' }}>
            Action
          </S.TaskItemText>
        </S.TaskItemWrapper>
        <Divider />

        {/* Table body */}
        {displayedInboundInvoice.items?.map((item, index) => (
          <ItemRow
            item={item}
            onCheckboxClick={handleCheckItem}
            checked={checkedItems.some(
              (checkedItem) =>
                checkedItem.sku_code === item.sku_code &&
                checkedItem.foc === item.foc,
            )}
            handleSetItemDetail={setShowItemDetail}
            index={index}
            inboundTaskNumber={inboundInvoice.inbound_task_number}
            invoiceNumber={invoiceNumber}
          />
        ))}
      </S.ContentWrapper>

      {/* Footer */}
      <S.StickyFooterWrapper>
        <S.ConfirmationButton
          onClick={() => {
            setOpenConfirmationDialog(true);
          }}
          disabled={displayedInboundInvoice.items?.some(
            (item) =>
              ![
                InboundTaskItemStatusEnum.APPROVED,
                InboundTaskItemStatusEnum.REJECTED,
              ].includes(item.status),
          )}
        >
          Confirm
        </S.ConfirmationButton>
      </S.StickyFooterWrapper>

      {/* Bulk confirmation dialog */}
      <ConfirmationDialog
        title={openBulkDialogData?.title || ''}
        content={openBulkDialogData?.content(selectedSkus) || ''}
        onClose={() => handleCloseBulkDialog()}
        openModal={openBulkDialog}
        onConfirm={() =>
          openBulkDialogData?.onConfirm(
            dispatch,
            displayedInboundInvoice.inbound_task_number,
            displayedInboundInvoice.invoice_number,
            checkedItems,
          )
        }
      />

      {/* Invoice confirmation dialog */}
      <ConfirmationDialog
        title="Confirm Approval"
        content={`Are you sure to proceed with the approval for invoice ${displayedInboundInvoice.invoice_number}?`}
        onClose={() => setOpenConfirmationDialog(false)}
        openModal={openConfirmationDialog}
        onConfirm={() =>
          dispatch(
            completeInboundInvoiceApprovalAction({
              payload: {
                items: (displayedInboundInvoice.items || []).map((item) => ({
                  sku_code: item.sku_code,
                  foc: item.foc,
                })),
                invoice_number: invoiceNumber,
                inbound_task_number: inboundTaskNumber,
              },
              postAction: () => navigate(INBOUND_ROUTES.APPROVAL_LIST),
            }),
          )
        }
      />

      {/* Item bin details modal */}
      <ItemBinDetailsModal
        item={showItemDetail}
        onClose={() => setShowItemDetail(null)}
      />
    </div>
  );
};
